import { useCallback, useMemo } from 'react';
import { DataGridPremium, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { ConditionsNew } from '@retail/calculus/types';
import { dataGridBoldClass } from '@shared/styles';
import { AddButton, NoTableRowsOverlay, StackedTextCell } from '@retail/components';
import { Box } from '@mui/material';
import { getDataGridLocalization, getLLDate, MgPrisLocale } from '@retail/utils';
import { ConditionsTableActions, LevelChip } from '@retail/calculus/components';
import { getHierarchyLevelLabel } from '@retail/calculus/utils';
import { useAppTFunction } from '@retail/app/i18n';
import { useRowColorGrouping } from '@retail/hooks';

interface Props {
  language: string;
  loading: boolean;
  conditions: ConditionsNew[];
  conditionActions?: {
    setEdit: (condition: ConditionsNew) => void;
    setDeactivate: (conditionId?: number) => void;
    setDelete: (conditionId?: number) => void;
  };
  openCreateDialog?: () => void;
  renderAffectedItemsCell: (params: GridRenderCellParams<ConditionsNew>) => React.ReactNode;
}

const baseColumnProps: Partial<GridColDef> = {
  width: 140,
  sortable: false,
  headerName: '',
  headerAlign: 'left',
  align: 'left',
};

export const FreightConditionsTable = ({
  language,
  loading,
  conditions,
  conditionActions,
  openCreateDialog,
  renderAffectedItemsCell,
}: Props) => {
  const t = useAppTFunction();
  const { getRowColorForStripedTable } = useRowColorGrouping();

  const getFreightText = useCallback(
    (row: ConditionsNew) => {
      if (row.freightCondition?.percentAddon) {
        return row.freightCondition?.percentAddon + '%';
      } else if (row.freightCondition?.fixedAddOn) {
        return t('freight.columns.pricePerUnit', {
          price: row.freightCondition.fixedAddOn,
          unit: row.freightCondition.fixedAddOnUnit,
        });
      }
      return '';
    },
    [t]
  );

  const columns: GridColDef<ConditionsNew>[] = useMemo(
    () => [
      {
        ...baseColumnProps,
        field: 'level',
        headerName: t('freight.columns.level'),
        width: 190,
        cellClassName: () => dataGridBoldClass,
        valueGetter: (_, row) =>
          getHierarchyLevelLabel({
            t,
            bindingType: row.bindingType,
            packageType: row.bindingTargetItem?.packageType,
          }),
        renderCell: ({ row }) => {
          return (
            <LevelChip
              bindingType={row.bindingType}
              active={!row.inDraft}
              bindingTargetItem={row.bindingTargetItem}
            />
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'name',
        headerName: t('freight.columns.name'),
        width: 260,
        cellClassName: () => dataGridBoldClass,
        valueGetter: (_, row) => row.bindingTargetName,
        renderCell: ({ row }) => (
          <StackedTextCell topLine={row.bindingTargetName} bottomLine={row.bindingTargetId} />
        ),
      },
      {
        ...baseColumnProps,
        field: 'validFromTo',
        headerName: t('freight.columns.activeFromTo'),
        valueGetter: (_, row) =>
          `${getLLDate(row.freightCondition?.validFrom)}-${
            getLLDate(row.freightCondition?.validTo) ?? ''
          }`,
        renderCell: ({ row }) => {
          const activeFrom = getLLDate(row.freightCondition?.validFrom) ?? '-';
          const activeTo = getLLDate(row.freightCondition?.validTo);
          return <StackedTextCell topLine={activeFrom} bottomLine={activeTo} centerAlign />;
        },
      },
      {
        ...baseColumnProps,
        field: 'freightValue',
        headerName: t('freight.columns.freightValue'),
        valueGetter: (_, row) => getFreightText(row),
        renderCell: ({ row }) => getFreightText(row),
      },
      {
        ...baseColumnProps,
        field: 'itemsAffected',
        headerName: t('freight.columns.itemsAffected'),
        width: 130,
        headerAlign: 'center',
        align: 'center',
        renderCell: renderAffectedItemsCell,
      },
      {
        ...baseColumnProps,
        field: 'actions',
        width: 90,
        disableColumnMenu: true,
        renderCell: ({ row }) =>
          conditionActions ? (
            <ConditionsTableActions
              handleEdit={() => conditionActions.setEdit(row)}
              handleDeactivate={
                !row.inDraft
                  ? () => conditionActions.setDeactivate(row.freightCondition?.conditionId)
                  : undefined
              }
              handleDelete={
                row.inDraft
                  ? () => conditionActions.setDelete(row.freightCondition?.conditionId)
                  : undefined
              }
            />
          ) : (
            ''
          ),
      },
    ],
    [t, renderAffectedItemsCell, getFreightText, conditionActions]
  );

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <DataGridPremium
          getRowId={(row) => row.freightCondition!.conditionId}
          columns={columns}
          rows={conditions}
          getRowClassName={getRowColorForStripedTable}
          rowHeight={70}
          loading={loading}
          disableRowSelectionOnClick
          disableColumnResize
          disableColumnReorder
          disableColumnMenu
          hideFooter
          localeText={getDataGridLocalization(language as MgPrisLocale)}
          initialState={{ pinnedColumns: { right: ['actions'] } }}
          slots={{
            noRowsOverlay: () => (
              <NoTableRowsOverlay
                noRowsText={t('freight.noFreightCondtitions')}
                addAction={
                  <AddButton onClick={openCreateDialog} disabled={!openCreateDialog}>
                    {t('freight.createFreightCondition')}
                  </AddButton>
                }
              />
            ),
          }}
        />
      </Box>
    </Box>
  );
};
